import type { HalebopRequestErrorData } from '~/repository/factory';
import type { ShippingMethod } from '~/repository/modules/ShippingMethod.module';
import { isTestEnvironment } from '~/utils/general/isTestEnvironment';

export const useFetchAddressAndShipping = () => {
  const { $api } = useNuxtApp();
  const checkoutStore = useMyCheckoutStore();
  const notificationStore = useNotificationStore();

  const fetchDeliveryAddress = async (
    testPersonaSsn?: string,
    isUsingBankidReplacement = false,
  ) => {
    if (isUsingBankidReplacement) {
      checkoutStore.ui.bankIdTestPassthroughIsSubmitting = true;
    }

    const allowBankidReplacement =
      isTestEnvironment() && isUsingBankidReplacement;

    try {
      if (allowBankidReplacement) {
        if (!testPersonaSsn) {
          throw createError('Inget personnummer valt för testperson.');
        }

        await $api.CustomerModule.setSsnForTestEnvironmentBankIdPassthrough(
          testPersonaSsn,
        );
      }

      checkoutStore.ui.customerRequiresAuth = false;
      checkoutStore.ui.loading.isStep3AddressLoading = true;

      const res = await $api.CustomerModule.getDeliveryAddress();
      const gdpr = await $api.CustomerModule.GetCustomerConsents();

      if (gdpr.CustomerConsents) {
        checkoutStore.customerConsents = gdpr.CustomerConsents;
      }

      return res;
    } catch (error) {
      await handleErrorsForFetchRequestsThatRequireBankIdAuthenticated(
        error,
        'Det går tyvärr inte att hämta din adress just nu vänligen, försök igen',
      );
    } finally {
      checkoutStore.ui.bankIdTestPassthroughIsSubmitting = false;
      checkoutStore.ui.loading.isStep3AddressLoading = false;
    }
  };

  const fetchShippingMethods = async (): Promise<ShippingMethod[]> => {
    checkoutStore.ui.loading.isStep3ShippingLoading = true;

    try {
      const shippingOpsResponse =
        await $api.shippingMethodModule.getShippingMethods();

      if (
        !Array.isArray(shippingOpsResponse) ||
        shippingOpsResponse?.length === 0
      ) {
        throw new Error();
      }

      shippingOpsResponse.forEach((option) => {
        if (option.ServicePoints.length > 0) {
          option.ServicePoints[0].Selected = true;
        }
      });

      return shippingOpsResponse;
    } catch (error) {
      await handleErrorsForFetchRequestsThatRequireBankIdAuthenticated(
        error,
        'Det går tyvärr inte att hämta leveransalternativ just nu, vänligen försök igen',
      );
      return [];
    } finally {
      checkoutStore.ui.loading.isStep3ShippingLoading = false;
    }
  };

  const setGeneralErrorNotification = (msg: string) => {
    notificationStore.setNewNotification({
      type: 'error',
      title: 'Ett fel uppstod',
      msg,
      isCloseable: true,
      link: {
        url: 'https://www.halebop.se/support',
        text: 'kontakta supporten',
      },
    });
  };

  const handleErrorsForFetchRequestsThatRequireBankIdAuthenticated = async (
    error: unknown,
    fallBackMessage: string,
  ) => {
    if (isNuxtError<HalebopRequestErrorData>(error)) {
      if (error.data?.errorCodeDetail === 'SsnAuthDifferentSsn') {
        checkoutStore.clearCartData();
        await checkoutStore.fetchCart();
        setGeneralErrorNotification(
          'Du har identifierat dig som en annan person än tidigare under det här besöket. Tyvärr har vi behövt rensa din varukorg åt dig.',
        );
        return;
      }

      if (error.data?.errorCodeDetail === 'TooYoung') {
        setGeneralErrorNotification(
          'Du behöver vara minst 18 år för att handla hos oss.',
        );
        checkoutStore.ui.customerRequiresAuth = true;
        return;
      }

      if (error.data?.errorCodeDetail === 'NotStudent') {
        setGeneralErrorNotification('Du verifierades inte som student.');
        checkoutStore.ui.customerRequiresAuth = true;
        return;
      }

      if (error.data?.errorCodeDetail === 'SsnAuthTimeoutExpired') {
        checkoutStore.ui.customerRequiresAuth = true;
        return;
      }

      setGeneralErrorNotification(fallBackMessage);
      checkoutStore.ui.customerRequiresAuth = true;
      return;
    }

    setGeneralErrorNotification(fallBackMessage);
  };

  const fetchSsnCensored = async () => {
    try {
      checkoutStore.ui.loading.isStep3SsnCensoredLoading = true;
      const ssnCensored = await $api.CustomerModule.GetCustomerSsnCensored();

      checkoutStore.fetchedData.ssnCensored = ssnCensored;
    } catch (e) {
      if (isNuxtError(e)) {
        logError(e);
      }
    } finally {
      checkoutStore.ui.loading.isStep3SsnCensoredLoading = false;
    }
  };

  const setUserInput = async (
    shippingMethod: ShippingMethod,
    inputtedEmailAddress: string,
  ) => {
    await Promise.all([
      $api.checkout.setDeliveryMethod(shippingMethod),
      $api.CustomerModule.SetEmailAddress(inputtedEmailAddress),
    ]);
  };

  return {
    fetchDeliveryAddress,
    fetchShippingMethods,
    fetchSsnCensored,
    setUserInput,
  };
};
